import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Star, Quote, PenLine, Loader2, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEED_REVIEWS = [
  { id: "s1", name: "Zaynab Alabdullah", rating: 5, text: "I had a great experience at Kitchen Box! The food was absolutely delicious — I tried the Iraqi Dolma, and it was full of flavor and perfectly cooked. Fresh ingredients, generous portions, and Raya was so friendly and welcoming. Well-seasoned and authentic. I would definitely come back again. Highly recommended!" },
  { id: "s2", name: "Sara M.", rating: 5, text: "Absolutely worth it! 😍 This was my first time eating here, and I loved everything about it. The food was incredibly delicious, juicy, and full of flavor 🤤🍽️. Every bite was amazing!" },
  { id: "s3", name: "Omar K.", rating: 5, text: "So happy to see an actual authentic Iraqi food in Charlottesville, VA." },
  { id: "s4", name: "Angela Canady", rating: 5, text: "Baklava was so good!!! Highly recommend." },
  { id: "s5", name: "Ali Yassen", rating: 5, text: "Great and delicious Eastern food, reasonable prices. Great food!" },
  { id: "s6", name: "Esraa Alasfar", rating: 5, text: "MashaAllah, very delicious, nice and clean food. I would highly recommend it." },
  { id: "s7", name: "Marcus D.", rating: 5, text: "The Iraqi Burger is a must-try — juicy, packed with flavor, and unlike anything else in Charlottesville. Fast pickup and super friendly service." },
  { id: "s8", name: "Layla H.", rating: 5, text: "Ordered for a family gathering and everyone was blown away. Authentic home-style cooking, generous portions, and the kunafa was the perfect finish. We'll be back!" },
];

const initials = (name) =>
  name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();

const Stars = ({ value = 5, size = 16 }) => (
  <div className="flex text-gold">
    {[...Array(5)].map((_, i) => (
      <Star key={i} size={size} fill={i < value ? "currentColor" : "none"} className={i < value ? "" : "text-gold/30"} strokeWidth={i < value ? 0 : 1.5} />
    ))}
  </div>
);

const ReviewDialog = ({ trigger }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", rating: 5, text: "" });
  const [hover, setHover] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.text.trim()) {
      toast.error("Please add your name and a short review.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, form);
      toast.success("Thank you! Your review was sent and will appear once approved. 🎉");
      setForm({ name: "", rating: 5, text: "" });
      setOpen(false);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-cream border-line rounded-3xl" data-testid="review-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-ink">Leave a review</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5 mt-2">
          <div>
            <Label className="text-ink font-semibold">Your name</Label>
            <Input
              data-testid="review-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Sara M."
              className="mt-2 bg-white border-line rounded-xl"
              maxLength={60}
            />
          </div>
          <div>
            <Label className="text-ink font-semibold">Your rating</Label>
            <div className="mt-2 flex gap-1" data-testid="review-rating-picker">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setForm({ ...form, rating: n })}
                  onMouseEnter={() => setHover(n)}
                  onMouseLeave={() => setHover(0)}
                  data-testid={`review-star-${n}`}
                  className="p-1 text-gold transition-transform hover:scale-110"
                >
                  <Star size={30} fill={(hover || form.rating) >= n ? "currentColor" : "none"} strokeWidth={(hover || form.rating) >= n ? 0 : 1.5} className={(hover || form.rating) >= n ? "" : "text-gold/40"} />
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-ink font-semibold">Your review</Label>
            <Textarea
              data-testid="review-text-input"
              value={form.text}
              onChange={(e) => setForm({ ...form, text: e.target.value })}
              placeholder="Tell us what you loved…"
              className="mt-2 bg-white border-line rounded-xl min-h-[110px]"
              maxLength={1000}
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="review-submit-btn"
            className="w-full inline-flex items-center justify-center gap-2 bg-brand text-white hover:bg-brand-hover transition-colors rounded-full px-7 py-3 text-sm font-bold shadow-md disabled:opacity-60"
          >
            {submitting ? <Loader2 size={17} className="animate-spin" /> : <CheckCircle2 size={17} />}
            {submitting ? "Posting…" : "Post my review"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const Reviews = () => {
  const [reviews, setReviews] = useState(SEED_REVIEWS);

  useEffect(() => {
    axios
      .get(`${API}/reviews`)
      .then(({ data }) => {
        if (Array.isArray(data) && data.length) {
          setReviews([...data, ...SEED_REVIEWS]);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section id="reviews" data-testid="reviews-section" className="py-20 sm:py-28 bg-surface2">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-sm tracking-[0.25em] uppercase text-brand font-bold">Reviews</span>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-ink">
            What our customers say
          </h2>
          <div className="mt-4 flex items-center justify-center gap-2" data-testid="reviews-rating-summary">
            <Stars value={5} size={20} />
            <p className="text-inkmuted text-sm font-semibold">Loved by locals in Charlottesville</p>
          </div>
          <ReviewDialog
            trigger={
              <button
                data-testid="leave-review-btn-top"
                className="mt-7 inline-flex items-center gap-2 bg-brand text-white hover:bg-brand-hover transition-colors rounded-full px-7 py-3 text-sm font-bold shadow-md"
              >
                <PenLine size={17} /> Leave us a review
              </button>
            }
          />
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {reviews.map((r, i) => (
            <motion.div
              key={r.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: (i % 3) * 0.1 }}
              data-testid={`review-card-${i + 1}`}
              className="bg-white p-7 rounded-3xl border border-line relative flex flex-col"
            >
              <Quote className="text-brand/20 absolute top-6 right-6" size={40} />
              <div className="mb-4"><Stars value={r.rating || 5} /></div>
              <p className="text-ink leading-relaxed flex-1">{r.text}</p>
              <div className="mt-6 flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-brand/10 text-brand font-serif font-bold flex items-center justify-center shrink-0">
                  {initials(r.name)}
                </div>
                <div>
                  <p className="font-semibold text-ink text-sm">{r.name}</p>
                  <p className="text-inkmuted text-xs">Verified customer</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-14 text-center">
          <p className="text-ink font-semibold">Tried KitchenBox? We'd love to hear from you!</p>
          <ReviewDialog
            trigger={
              <button
                data-testid="leave-review-btn-bottom"
                className="mt-4 inline-flex items-center gap-2 border-2 border-brand text-brand hover:bg-brand hover:text-white transition-colors rounded-full px-7 py-3 text-sm font-bold"
              >
                <PenLine size={17} /> Write a review
              </button>
            }
          />
        </div>
      </div>
    </section>
  );
};
